<template>
  <div>
    <div class="page-section">
      <div :class="containerClass">
        <!-- ============== Filters ============== -->
        <div class="card analytics-card toolbar-card mb-4">
          <div class="card-body">
            <div class="toolbar-controls">
              <div class="control control--md">
                <label>Date Range</label>
                <date-picker
                  v-model="filters.dateRange"
                  format="MMM DD, YYYY"
                  valueType="YYYY-MM-DD"
                  range
                  class="w-100"
                  placeholder="Start - End"
                />
              </div>
              <div class="control">
                <label>Course</label>
                <v-select
                  class="form-control v-select-custom"
                  label="title"
                  v-model="filters.course"
                  :reduce="o => o.id"
                  :options="allCourses"
                  placeholder="Select a course..."
                  :clearable="true"
                />
              </div>
              <div class="control">
                <label>Assessment</label>
                <!-- Gated on course: the assessment list is only meaningful once
                     there is a course to draw it from. -->
                <v-select
                  class="form-control v-select-custom"
                  label="quiz_title"
                  v-model="filters.assessment"
                  :reduce="o => o.quiz_id"
                  :options="assessmentOptions"
                  :disabled="!filters.course"
                  :loading="isAssessmentsLoading"
                  :clearable="true"
                  :placeholder="filters.course ? 'All assessments' : 'Select a course first'"
                >
                  <template #no-options>
                    No assessments have been attempted in this course.
                  </template>
                </v-select>
              </div>
            </div>
          </div>
        </div>

        <!-- ============== 1. Overview ============== -->
        <div class="card analytics-card mb-4">
          <div class="card-body">
            <div class="section-header">
              <div>
                <h5 class="section-title">Assessment Analytics Overview</h5>
                <p class="section-sub">{{ overviewSubtitle }}</p>
              </div>
            </div>

            <div v-if="isOverviewLoading" class="text-center py-4">
              <b-spinner class="align-middle mr-2" /><strong>Loading overview...</strong>
            </div>
            <div v-else-if="!overview.results_count" class="text-center text-muted py-4">
              No assessment attempts match the current filters.
            </div>
            <div v-else class="kpi-grid">
              <div v-for="card in kpiCards" :key="card.label" class="kpi-card">
                <div class="kpi-card__header">
                  <div :class="['kpi-card__icon', card.iconClass]">
                    <i class="material-icons">{{ card.icon }}</i>
                  </div>
                  <span class="kpi-card__label">{{ card.label }}</span>
                </div>
                <div class="kpi-card__value">
                  {{ card.value }}<span v-if="card.suffix" class="kpi-card__suffix">{{ card.suffix }}</span>
                </div>
                <div class="kpi-card__hint">{{ card.hint }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============== 2. Activity over time ============== -->
        <div class="card analytics-card mb-4">
          <div class="card-body">
            <div class="section-header">
              <div>
                <h5 class="section-title">Assessment Activity Over Time</h5>
                <p class="section-sub">
                  Submissions per period, including retakes.
                </p>
              </div>
              <div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
                <v-select
                  class="form-control v-select-custom chart-control"
                  label="label"
                  v-model="activity.timeFrame"
                  :reduce="o => o.value"
                  :options="timeFrameOptions"
                  :clearable="false"
                />
                <v-select
                  class="form-control v-select-custom chart-control"
                  label="quiz_title"
                  v-model="activity.quizId"
                  :reduce="o => o.quiz_id"
                  :options="allAssessments"
                  :clearable="true"
                  placeholder="All Assessments"
                />
              </div>
            </div>

            <div v-show="isActivityLoading" class="text-center py-4">
              <b-spinner class="align-middle mr-2" /><strong>Loading activity...</strong>
            </div>
            <!-- Kept mounted rather than v-if'd: Chart.js needs a canvas that
                 survives the loading state, otherwise it draws into a node that
                 was never in the document and the chart comes out blank. -->
            <div v-show="!isActivityLoading">
              <div class="chart-container">
                <canvas ref="activityCanvas"></canvas>
              </div>
              <p v-if="activityRangeLabel" class="chart-caption">{{ activityRangeLabel }}</p>
            </div>
          </div>
        </div>

        <!-- ============== 3. Most-missed questions ============== -->
        <div class="card analytics-card mb-4">
          <div class="card-body">
            <div class="section-header">
              <div>
                <h5 class="section-title">Most-Missed Questions</h5>
                <p class="section-sub">{{ missedSubtitle }}</p>
              </div>
            </div>

            <div v-if="isQuestionsLoading" class="text-center py-4">
              <b-spinner class="align-middle mr-2" /><strong>Loading question findings...</strong>
            </div>
            <div v-else-if="!missedQuestions.length" class="text-center text-muted py-4">
              No question is falling below the at-risk bar in this scope.
            </div>
            <div v-else class="missed-list">
              <div v-for="row in missedQuestions" :key="row.quiz_id + row.question_id" class="missed-row">
                <div class="missed-row__score">
                  <span class="missed-row__count">{{ formatInt(row.incorrect_count) }}</span>
                  <span class="missed-row__caption">
                    of {{ formatInt(row.attempted_count) }} students missed this
                  </span>
                </div>
                <div class="missed-row__body">
                  <p class="missed-row__question">{{ row.question_text }}</p>
                  <template v-if="row.top_distractor">
                    <p class="missed-row__answerlabel">Most common wrong answer</p>
                    <p class="missed-row__answer">{{ row.top_distractor }}</p>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============== 4. Detailed report ============== -->
        <div class="card analytics-card mb-4">
          <div class="card-body">
            <div class="section-header">
              <div>
                <h5 class="section-title">Generate Detailed Report</h5>
                <p class="section-sub">
                  A CSV over the current filters. Tick the sections and columns you need.
                </p>
              </div>
            </div>

            <b-btn
              variant="primary"
              class="d-inline-flex align-items-center"
              :disabled="isReportLoading || !anyReportSection"
              @click.prevent="generateReport"
            >
              <i v-if="isReportLoading" class="fas fa-circle-notch fa-spin mr-1"></i>
              <span v-else class="material-icons icon-16pt mr-1">file_download</span>
              Generate Report
            </b-btn>

            <div class="report-toggles mt-3">
              <b-form-checkbox
                v-for="opt in reportOptions"
                :key="opt.key"
                v-model="reportInclude[opt.key]"
              >
                {{ opt.label }}
              </b-form-checkbox>
            </div>

            <p v-if="!anyReportSection" class="report-warning mt-2">
              Pick at least one section to include.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { debounce } from 'lodash';
import DatePicker from 'vue2-datepicker';
import vSelect from 'vue-select';
import moment from 'moment';
import { Chart } from 'chart.js';
import Page from '@/components/Page.vue';
import { formatToAPIDate } from '../../../common/utils';

const defaultRange = () => [
  formatToAPIDate(moment().startOf('month')),
  formatToAPIDate(moment().endOf('month')),
];

// Fixed chart palette. The series hue is the validated categorical slot 1.
const SERIES_PRIMARY = '#2a78d6';
const GRID_COLOR = 'rgba(148, 163, 184, 0.2)';
const AXIS_TEXT = '#64748b';

// The most-missed panel reads raw question difficulty, so it counts each
// student's first sitting only - retakes would weight the students who tried
// hardest, not the questions that are hardest.
const MISSED_ATTEMPT_SCOPE = 'first';

const defaultReportInclude = () => ({
  quiz_scores: true,
  exam_scores: true,
  attempts_retakes: true,
  pass_fail_status: true,
  time_spent: true,
  per_question_breakdown: false,
  most_missed_items: false,
  completion_dates: false,
});

export default {
  components: { DatePicker, vSelect },
  extends: Page,
  data() {
    return {
      title: 'Assessment Analytics',

      isBooting: true,
      allCourses: [],

      filters: {
        dateRange: defaultRange(),
        course: null,
        assessment: null,
      },

      // Assessments within the selected course, for the page filter.
      assessmentOptions: [],
      isAssessmentsLoading: false,
      // Every assessment in scope, for the chart's own scope select.
      allAssessments: [],

      overview: {},
      isOverviewLoading: false,

      activity: { timeFrame: 'monthly', quizId: null },
      activityPayload: {},
      isActivityLoading: false,
      activityChart: null,

      timeFrameOptions: [
        { value: 'weekly', label: 'Weekly' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'yearly', label: 'Yearly' },
      ],

      questionPayload: {},
      isQuestionsLoading: false,

      reportOptions: [
        { key: 'quiz_scores', label: 'Quiz Scores' },
        { key: 'exam_scores', label: 'Exam Scores' },
        { key: 'attempts_retakes', label: 'Attempts & Retakes' },
        { key: 'pass_fail_status', label: 'Pass / Fail Status' },
        { key: 'time_spent', label: 'Time Spent' },
        { key: 'per_question_breakdown', label: 'Per-Question Breakdown' },
        { key: 'most_missed_items', label: 'Most-Missed Items' },
        { key: 'completion_dates', label: 'Completion Dates' },
      ],
      reportInclude: defaultReportInclude(),
      isReportLoading: false,
    };
  },
  computed: {
    kpiCards() {
      const o = this.overview;
      return [
        {
          label: 'Assessments Taken',
          icon: 'assignment',
          iconClass: 'kpi-card__icon--grey',
          value: this.formatInt(o.results_count),
          hint: 'quizzes, exams & tests',
        },
        {
          label: 'Pass Rate',
          icon: 'check_circle',
          iconClass: 'kpi-card__icon--grey',
          value: this.formatPct(o.pass_rate),
          suffix: '%',
          hint: "met the assessment's pass mark",
        },
        {
          label: 'Fail Rate',
          icon: 'cancel',
          iconClass: 'kpi-card__icon--grey',
          value: this.formatPct(o.failure_rate),
          suffix: '%',
          hint: "under the assessment's pass mark",
        },
        {
          label: 'Avg Score',
          icon: 'grade',
          iconClass: 'kpi-card__icon--grey',
          value: this.formatPct(o.avg_best_percent),
          suffix: '%',
          // Not the mean of every attempt - the backend averages each student's
          // best one. Labelled so the tile is not read as something it isn't.
          hint: 'best score per student',
        },
        {
          label: 'Passed',
          icon: 'check_circle_outline',
          iconClass: 'kpi-card__icon--grey',
          value: this.formatInt(o.passed_count),
          hint: 'at or above the pass mark',
        },
        {
          label: 'Failed',
          icon: 'error_outline',
          iconClass: 'kpi-card__icon--grey',
          value: this.formatInt(o.failed_count),
          hint: 'below the pass mark',
        },
        {
          label: 'Avg Attempts',
          icon: 'repeat',
          iconClass: 'kpi-card__icon--grey',
          value: this.formatPct(o.avg_attempts),
          hint: 'per assessment',
        },
        {
          label: 'Retake Rate',
          icon: 'autorenew',
          iconClass: 'kpi-card__icon--grey',
          value: this.formatPct(o.retake_rate),
          suffix: '%',
          hint: 'needed more than one try',
        },
      ];
    },
    overviewSubtitle() {
      const o = this.overview;
      if (!o.results_count) return 'Pass and fail use each assessment’s own pass mark.';
      return `${this.formatInt(o.quizzes_assessed)} assessments across `
        + `${this.formatInt(o.students_assessed)} students. `
        + 'Pass and fail use each assessment’s own pass mark.';
    },
    activityRangeLabel() {
      const p = this.activityPayload;
      if (!p.date_from || !p.date_to) return '';
      return `(${p.date_from} → ${p.date_to})`;
    },
    missedQuestions() {
      return this.questionPayload.questions || [];
    },
    missedSubtitle() {
      const threshold = this.questionSummary.risk_threshold;
      const scope = this.filters.assessment ? 'this assessment' : 'every assessment in scope';
      if (!threshold) return `First-attempt accuracy across ${scope}.`;
      return `First-attempt accuracy below the ${this.formatPct(threshold)}% bar, `
        + `across ${scope}.`;
    },
    questionSummary() {
      return this.questionPayload.summary || {};
    },
    anyReportSection() {
      return Object.values(this.reportInclude).some(Boolean);
    },
    baseParams() {
      const range = this.filters.dateRange || [];
      const params = {
        date_from: range[0] || null,
        date_to: range[1] || null,
      };
      if (this.filters.course) params.course_id = this.filters.course;
      if (this.filters.assessment) params.quiz_id = this.filters.assessment;
      return params;
    },
  },
  methods: {
    ...mapActions('lmsAnalytics', [
      'getQuizAnalyticsOverview',
      'getQuizLeaderboard',
      'getQuizQuestionBreakdown',
      'getAssessmentActivityGraph',
      'generateAssessmentReport',
    ]),
    ...mapActions('courses', ['getLmsCourses']),
    ...mapActions('fileDownload', ['downloadFile']),

    formatPct(v) {
      if (v === null || v === undefined || v === '') return '0';
      const n = Number(v);
      return Number.isNaN(n) ? '0' : n.toFixed(1);
    },
    formatInt(v) {
      if (v === null || v === undefined || v === '') return '0';
      const n = Number(v);
      return Number.isNaN(n) ? '0' : n.toLocaleString();
    },
    unwrapEnvelope(res) {
      const payload = res && res.data !== undefined ? res.data : res;
      return payload || {};
    },
    unwrapListEnvelope(res) {
      const payload = res && res.data ? res.data : res;
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload.results)) return payload.results;
      if (payload.data) {
        if (Array.isArray(payload.data)) return payload.data;
        if (Array.isArray(payload.data.results)) return payload.data.results;
      }
      return [];
    },
    unwrapUrl(res) {
      return (res && res.data && res.data.url) || (res && res.url) || null;
    },
    validateRange(maxMonths = 24) {
      const r = this.filters.dateRange;
      if (!r || !r[0] || !r[1]) return true;
      const diff = moment(r[1]).diff(moment(r[0]), 'months', true);
      if (diff > maxMonths) {
        this.makeToast({ variant: 'danger', msg: `Date range must be ${maxMonths} months or less.` });
        return false;
      }
      return true;
    },
    async runDownload(url) {
      try {
        await this.downloadFile({ fileUrl: url });
        this.makeToast({ variant: 'success', msg: 'File downloaded.' });
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to download file.' });
      }
    },

    // -------- Lookups --------
    async fetchLookups() {
      try {
        const coursesRes = await this.getLmsCourses({ limit: 200 });
        this.allCourses = this.unwrapListEnvelope(coursesRes).map(c => ({ id: c.id, title: c.title }));
      } catch (e) { /* silent */ }
    },

    // -------- Assessment pickers --------
    async fetchAssessments() {
      if (!this.validateRange(24)) return;
      this.isAssessmentsLoading = true;
      try {
        const range = this.filters.dateRange || [];
        const scopeParams = { date_from: range[0] || null, date_to: range[1] || null };

        const allRes = await this.getQuizLeaderboard(scopeParams);
        this.allAssessments = this.unwrapListEnvelope(allRes);

        if (this.filters.course) {
          const courseRes = await this.getQuizLeaderboard({
            ...scopeParams, course_id: this.filters.course,
          });
          this.assessmentOptions = this.unwrapListEnvelope(courseRes);
        } else {
          this.assessmentOptions = [];
        }

        // Drop a selection that has fallen out of the current course/date scope,
        // rather than filtering everything by an assessment no longer listed.
        if (this.filters.assessment
          && !this.assessmentOptions.some(r => r.quiz_id === this.filters.assessment)) {
          this.filters.assessment = null;
        }
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load assessments.' });
      }
      this.isAssessmentsLoading = false;
    },

    // -------- Overview --------
    async fetchOverview() {
      if (!this.validateRange(24)) return;
      this.isOverviewLoading = true;
      try {
        const res = await this.getQuizAnalyticsOverview(this.baseParams);
        this.overview = this.unwrapEnvelope(res);
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load the assessment overview.' });
      }
      this.isOverviewLoading = false;
    },

    // -------- Activity --------
    async fetchActivity() {
      if (!this.validateRange(24)) return;
      this.isActivityLoading = true;
      try {
        // The chart carries its own assessment scope select, so it takes the
        // course/date scope from the page but ignores the page's assessment
        // filter - two controls pointing at the same axis would be ambiguous.
        const params = { ...this.baseParams, time_frame: this.activity.timeFrame };
        delete params.quiz_id;
        if (this.activity.quizId) params.quiz_id = this.activity.quizId;
        const res = await this.getAssessmentActivityGraph(params);
        this.activityPayload = this.unwrapEnvelope(res);
        this.$nextTick(this.renderActivityChart);
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load assessment activity.' });
      }
      this.isActivityLoading = false;
    },

    // -------- Most-missed questions --------
    async fetchQuestionBreakdown() {
      if (!this.validateRange(24)) return;
      this.isQuestionsLoading = true;
      try {
        const res = await this.getQuizQuestionBreakdown({
          ...this.baseParams,
          attempt_scope: MISSED_ATTEMPT_SCOPE,
        });
        this.questionPayload = this.unwrapEnvelope(res);
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load question findings.' });
      }
      this.isQuestionsLoading = false;
    },

    // -------- Report --------
    async generateReport() {
      if (!this.validateRange(24)) return;
      this.isReportLoading = true;
      try {
        const res = await this.generateAssessmentReport({
          ...this.baseParams,
          include: this.reportInclude,
        });
        const url = this.unwrapUrl(res);
        if (url) await this.runDownload(url);
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to generate the assessment report.' });
      }
      this.isReportLoading = false;
    },

    // -------- Charts --------
    renderActivityChart() {
      const ctx = this.$refs.activityCanvas;
      if (!ctx) return;
      if (this.activityChart) this.activityChart.destroy();

      const payload = this.activityPayload;
      this.activityChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: payload.labels || [],
          datasets: [{
            label: 'Assessments Completed',
            data: payload.datasets || [],
            backgroundColor: SERIES_PRIMARY,
            barPercentage: 0.6,
            categoryPercentage: 0.8,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { position: 'top' },
          scales: {
            yAxes: [{
              ticks: { beginAtZero: true, fontColor: AXIS_TEXT },
              gridLines: { color: GRID_COLOR, drawBorder: false },
            }],
            xAxes: [{
              ticks: { fontColor: AXIS_TEXT },
              gridLines: { display: false },
            }],
          },
        },
      });
    },
    destroyCharts() {
      if (this.activityChart) {
        try { this.activityChart.destroy(); } catch (e) { /* noop */ }
        this.activityChart = null;
      }
    },

    refreshAll() {
      this.fetchOverview();
      this.fetchActivity();
      this.fetchQuestionBreakdown();
    },

    // -------- Filter watchers --------
    // All three bail while booting: seeding the filters from the query string
    // trips the watchers before the first load has run, and without the guard
    // every endpoint is hit twice with the later response winning.
    onScopeChange() {
      if (this.isBooting) return;
      this.fetchAssessments();
      this.debouncedRefreshAll();
    },
    onCourseChange() {
      if (this.isBooting) return;
      // A course change invalidates the assessment list, so drop the selection
      // rather than filtering by an assessment from a different course. That
      // write re-enters onFilterChange, which the debounce collapses.
      this.filters.assessment = null;
      this.fetchAssessments();
      this.debouncedRefreshAll();
    },
    onFilterChange() {
      if (this.isBooting) return;
      this.debouncedRefreshAll();
    },
  },
  watch: {
    'filters.dateRange': 'onScopeChange',
    'filters.course': 'onCourseChange',
    'filters.assessment': 'onFilterChange',
    'activity.timeFrame': 'fetchActivity',
    'activity.quizId': 'fetchActivity',
  },
  created() {
    this.debouncedRefreshAll = debounce(this.refreshAll, 250);
  },
  async mounted() {
    const q = this.$route.query || {};
    if (q.date_from && q.date_to) this.filters.dateRange = [q.date_from, q.date_to];
    if (q.course_id) this.filters.course = q.course_id;

    await this.fetchLookups();
    await this.fetchAssessments();

    // Deep link straight into one assessment.
    if (q.quiz_id && this.assessmentOptions.some(r => r.quiz_id === q.quiz_id)) {
      this.filters.assessment = q.quiz_id;
    }

    // Let the query-string writes above flush through the watchers while they
    // are still guarded, then take the first load ourselves.
    await this.$nextTick();
    this.isBooting = false;
    this.refreshAll();
  },
  beforeDestroy() {
    this.destroyCharts();
  },
};
</script>

<style scoped lang="scss">
.analytics-card {
  border: 1px solid #e7ecf5;
  border-radius: 16px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.05);
  background: #fff;
}

.toolbar-card { position: sticky; top: 8px; z-index: 5; }

/* ---- toolbar ---- */
.toolbar-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.control {
  flex: 1 1 170px;
  min-width: 160px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #475569;
    margin: 0;
  }
}
.control--md { flex: 1 1 220px; min-width: 200px; }

/* ---- KPI tiles ---- */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 16px;
}
.kpi-card {
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  border: 1px solid #eef2f7;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 12px 22px rgba(15, 23, 42, 0.08); }
}
.kpi-card__header { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.kpi-card__icon {
  width: 36px; height: 36px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  flex: 0 0 auto;
  i { font-size: 18px; color: #fff; }
  &--blue   { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
  &--green  { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
  &--red    { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
  &--purple { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }
  &--orange { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
  &--grey   { background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%); }
}
.kpi-card__label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.kpi-card__value {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.kpi-card__suffix { font-size: 0.95rem; color: #6b7280; }
.kpi-card__hint { margin-top: 6px; font-size: 0.74rem; color: #94a3b8; }

/* ---- sections ---- */
.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 16px;
  flex-wrap: wrap;
}
.section-title {
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 4px;
  font-size: 1.05rem;
  letter-spacing: 0.01em;
}
.section-sub { margin: 0; color: #64748b; font-size: 0.85rem; }

.chart-control { width: 170px; min-width: 150px; }
.chart-container { position: relative; height: 320px; }
.chart-caption {
  margin: 8px 0 0;
  text-align: center;
  font-size: 0.76rem;
  color: #94a3b8;
}

/* ---- most-missed list ---- */
.missed-list { display: flex; flex-direction: column; gap: 16px; }
.missed-row {
  display: flex;
  align-items: flex-start;
  gap: 28px;
  padding: 26px 28px;
  background: #fff;
  border: 1px solid #edf0f4;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 20px rgba(15, 23, 42, 0.04);
}
.missed-row__score {
  flex: 0 0 150px;
  display: flex;
  flex-direction: column;
  padding-right: 28px;
  border-right: 1px solid #e8ecf2;
  align-self: stretch;
}
.missed-row__count {
  font-size: 2.6rem;
  font-weight: 800;
  color: #c0392b;
  line-height: 1;
  letter-spacing: -0.02em;
}
.missed-row__caption {
  margin-top: 10px;
  font-size: 0.86rem;
  line-height: 1.35;
  color: #64748b;
}
.missed-row__body { flex: 1 1 auto; min-width: 0; }
.missed-row__question {
  margin: 0 0 14px;
  font-size: 1.06rem;
  font-weight: 700;
  line-height: 1.35;
  color: #12161c;
}
.missed-row__answerlabel {
  margin: 0 0 4px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #c0392b;
}
.missed-row__answer {
  margin: 0;
  font-size: 1rem;
  line-height: 1.4;
  color: #334155;
}

@media (max-width: 575.98px) {
  .missed-row {
    flex-direction: column;
    gap: 14px;
  }
  .missed-row__score {
    flex: none;
    padding-right: 0;
    border-right: 0;
  }
}

/* ---- report ---- */
.report-toggles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px 16px;
}
.report-warning { font-size: 0.8rem; color: #b91c1c; margin: 0; }
</style>
